import { type ReactNode } from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Flex,
  type FlexProps,
  Skeleton,
  Stack,
  Text,
  type TextProps,
  Wrap,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuRefreshCw } from 'react-icons/lu';

export type DataListProps = FlexProps;

export const DataList = (props: DataListProps) => {
  return (
    <Flex
      flexDirection="column"
      position="relative"
      boxShadow="none"
      borderRadius="none"
      overflowX="auto"
      overflowY="hidden"
      minH="8.5rem"
      bg="white"
      border="1px solid"
      borderColor="gray.50"
      {...props}
    />
  );
};

export type DataListRowProps = FlexProps & { withHover?: boolean };

export const DataListRow = ({ withHover, ...props }: DataListRowProps) => {
  return (
    <Flex
      borderBottom="1px solid"
      borderBottomColor="gray.50"
      transition="0.2s"
      px={1.5}
      {...props}
      _last={{
        // Hide bottom border, if the row is at the bottom of the DataList
        mb: '-1px',
        ...props._last,
      }}
      _hover={{
        ...(withHover
          ? { bg: 'gray.50', _dark: { bg: 'whiteAlpha.100' } }
          : {}),
        ...props._hover,
      }}
      _dark={{
        borderBottomColor: 'gray.900',
        ...props._dark,
      }}
    />
  );
};

export type DataListCellProps = FlexProps;

export const DataListCell = (props: DataListCellProps) => {
  const isFluid = props.w === undefined && props.width === undefined;

  return (
    <Flex
      flexDirection="column"
      minW={0}
      flex={isFluid ? 1 : undefined}
      px="6"
      h="full"
      minH="14"
      align="flex-start"
      justifyContent="center"
      borderRight="1px solid"
      borderRightColor="gray.50"
      _last={{
        borderRight: 'none', // Remove right border for the last cell in the row
      }}
      {...props}
    >
      {props.children}
    </Flex>
  );
};

export type DataListTextHeaderProps = DataListTextProps;

export const DataListTextHeader = (props: DataListTextHeaderProps) => {
  return (
    <DataListText fontSize="sm" fontWeight="400" color="text-dimmed" {...props}>
      {props.children}
    </DataListText>
  );
};

export type DataListTextProps = TextProps;

export const DataListText = (props: DataListTextProps) => {
  return (
    <Text
      as="div"
      fontWeight="500"
      fontSize="md"
      maxW="full"
      overflowWrap="break-word"
      noOfLines={[1, 2]}
      {...props}
    />
  );
};

export const DataListLoadingState = () => {
  return (
    <>
      <DataListRow>
        <DataListCell>
          <Stack w="full" opacity={0.6} p={2}>
            <Skeleton w="30%" h={2} noOfLines={1} />
            <Skeleton w="20%" h={2} noOfLines={1} />
          </Stack>
        </DataListCell>
      </DataListRow>
      <DataListRow>
        <DataListCell>
          <Stack w="full" opacity={0.4} p={2}>
            <Skeleton w="30%" h={2} noOfLines={1} />
            <Skeleton w="20%" h={2} noOfLines={1} />
          </Stack>
        </DataListCell>
      </DataListRow>
      <DataListRow>
        <DataListCell>
          <Stack w="full" opacity={0.2} p={2}>
            <Skeleton w="30%" h={2} noOfLines={1} />
            <Skeleton w="20%" h={2} noOfLines={1} />
          </Stack>
        </DataListCell>
      </DataListRow>
    </>
  );
};

export type DataListEmptyStateProps = {
  children?: ReactNode;
  searchTerm?: string;
  label?: string;
};

export const DataListEmptyState = (props: DataListEmptyStateProps) => {
  const { t } = useTranslation(['components']);
  return (
    <DataListRow flex={1}>
      <DataListCell
        flex={1}
        justifyContent="center"
        alignItems="center"
        fontSize="sm"
        fontWeight="semibold"
        color="text-dimmed"
      >
        {props.searchTerm ? (
          <Box>
            {t('components:datalist.noResultsTitle', {
              searchTerm: props.searchTerm,
            })}
          </Box>
        ) : (
          (props.children ?? (
            <Box>{props.label ?? t('components:datalist.emptyTitle')}</Box>
          ))
        )}
      </DataListCell>
    </DataListRow>
  );
};

export type DataListErrorStateProps = {
  title?: ReactNode;
  children?: ReactNode;
  retry?: () => void;
};

export const DataListErrorState = (props: DataListErrorStateProps) => {
  const { t } = useTranslation(['components']);
  return (
    <DataListRow>
      <DataListCell>
        <Alert status="error">
          <AlertTitle>
            {props.title ?? t('components:datalist.errorTitle')}
          </AlertTitle>
          {(!!props.children || !!props.retry) && (
            <AlertDescription>
              <Wrap spacingX={2} spacingY={1}>
                {!!props.children && (
                  <Box alignSelf="center">{props.children}</Box>
                )}
                {!!props.retry && (
                  <Button
                    colorScheme="error"
                    variant="ghost"
                    size="sm"
                    leftIcon={<LuRefreshCw />}
                    onClick={() => props.retry?.()}
                  >
                    {t('components:datalist.retry')}
                  </Button>
                )}
              </Wrap>
            </AlertDescription>
          )}
        </Alert>
      </DataListCell>
    </DataListRow>
  );
};

export const ListSkeleton = () => {
  return (
    <>
      <Skeleton h="14" mb={1} />
      <Skeleton h="14" mb={1} />
      <Skeleton h="14" />
    </>
  );
};
